import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../services/api";
import { useSocket } from "./useSocket";

export interface Message {
  messageId: string;
  conversationId: string;
  senderId: string;
  body: string;
  deletedAt: string | null;
  createdAt: string;
}

interface UseMessagesResult {
  messages: Message[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  sendMessage: (body: string) => Promise<void>;
}

/**
 * Hook for managing message history with cursor-based pagination
 * and real-time updates via WebSocket.
 *
 * @param conversationId - The active conversation ID
 * @returns Message state and control functions
 */
export function useMessages(conversationId: string | null): UseMessagesResult {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const { subscribe, emit } = useSocket();
  const conversationIdRef = useRef(conversationId);

  // Keep ref in sync
  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  // Fetch messages for a conversation
  const fetchMessages = useCallback(
    async (cursorValue: string | null, append: boolean) => {
      if (!conversationId) return;
      setLoading(true);
      setError(null);

      try {
        const response = await api.messaging.getMessages(conversationId, cursorValue || undefined);

        if (append) {
          // Prepend older messages (cursor pagination loads older messages)
          setMessages((prev) => {
            const existingIds = new Set(prev.map((m) => m.messageId));
            const newMessages = response.items.filter(
              (m: Message) => !existingIds.has(m.messageId)
            );
            return [...newMessages, ...prev];
          });
        } else {
          setMessages(response.items);
        }

        setCursor(response.cursor);
        setHasMore(response.hasMore);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to load messages";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [conversationId]
  );

  // Load more (older) messages
  const loadMore = useCallback(() => {
    if (!hasMore || loading) return;
    fetchMessages(cursor, true);
  }, [hasMore, loading, cursor, fetchMessages]);

  // Send a message
  const sendMessage = useCallback(
    async (body: string) => {
      if (!conversationId) return;
      try {
        const response = await api.messaging.sendMessage(conversationId, body);
        // Append the sent message to the list
        setMessages((prev) => {
          const exists = prev.some((m) => m.messageId === response.messageId);
          if (exists) return prev;
          return [...prev, response];
        });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to send message";
        throw new Error(errorMessage);
      }
    },
    [conversationId]
  );

  // Reset state when conversation changes
  useEffect(() => {
    setMessages([]);
    setCursor(null);
    setHasMore(false);
    setError(null);
    if (conversationId) {
      fetchMessages(null, false);
    }
  }, [conversationId, fetchMessages]);

  // Subscribe to real-time new_message events
  useEffect(() => {
    const unsub = subscribe("new_message", (msg: Message) => {
      if (msg.conversationId === conversationIdRef.current) {
        setMessages((prev) => {
          const exists = prev.some((m) => m.messageId === msg.messageId);
          if (exists) return prev;
          return [...prev, msg];
        });
      }
    });
    return unsub;
  }, [subscribe]);

  // Subscribe to message_deleted events
  useEffect(() => {
    const unsub = subscribe("message_deleted", (data: { messageId: string; conversationId: string }) => {
      if (data.conversationId === conversationIdRef.current) {
        setMessages((prev) =>
          prev.map((m) =>
            m.messageId === data.messageId
              ? { ...m, body: "", deletedAt: new Date().toISOString() }
              : m
          )
        );
      }
    });
    return unsub;
  }, [subscribe]);

  // Join/leave conversation room for typing indicators
  useEffect(() => {
    if (!conversationId) return;
    emit("join_conversation", { conversationId });
    return () => {
      emit("leave_conversation", { conversationId });
    };
  }, [conversationId, emit]);

  return {
    messages,
    loading,
    error,
    hasMore,
    loadMore,
    sendMessage,
  };
}
